import { REALM_FQDN_PLACEHOLDER } from "@visioni/a3s-environments";

const MIN = 0x01 as const;
const MAX = 0xff as const;

declare const RealmIdBrand: unique symbol;

/**
 * A unique identifier for an A3S Realm.
 */
export type RealmId = number & { readonly [RealmIdBrand]: "RealmId" };

export class RealmIdError extends Error {
  constructor(
    public readonly kind: "OutOfRange" | "Format" | "Exhausted",
    message: string,
  ) {
    super(message);
  }
}

/** Constructs a RealmId from a number or throws a RealmIdError. */
const fromNumber = (n: number): RealmId => {
  if (!Number.isInteger(n) || n < MIN || n > MAX)
    throw new RealmIdError("OutOfRange", `RealmId ${String(n)} is out of range. Expected 0x01 to 0xFF.`);

  return n as RealmId;
};

const toNumber = (realm: RealmId): number => realm;

/** Canonical encoding is 'R' + two uppercase hex digits. */
const toString = (id: RealmId): string => {
  const hex = (id as number).toString(16).toUpperCase().padStart(2, "0");
  return `R${hex}`;
};

/** Returns the lowercase subdomain form of a RealmId (e.g., "r01"). */
const toSubdomain = (id: RealmId): string => toString(id).toLowerCase();

/** Base path for realm API endpoints. */
const API_PATH = "/api" as const;

/** Base path for realm web app. */
const APP_PATH = "/app" as const;

/** Base path for realm MCP endpoint. */
const MCP_PATH = "/mcp" as const;

/** Base path for realm TUI launcher scripts (run.sh, run.ps1). */
const TUI_PATH = "/tui" as const;

/**
 * Constructs the full realm FQDN for a realm in a resolved environment.
 *
 * Prefer passing a `realmFqdn` template from a resolved {@link EnvironmentEntry} — this
 * is the source of truth for realm FQDN shape across all environments (including MR, where
 * realms are siblings of the root zone rather than children, e.g.
 * `"628-rff.a3s-int.visioni.network"`).
 *
 * @example
 *   const env = resolveEnvironment("mr", { mergeRequestId: "628" });
 *   RealmId.toFqdn(realm, env.realmFqdn); // "628-rff.a3s-int.visioni.network"
 */
const toFqdn = (id: RealmId, realmFqdnTemplate: string): string =>
  realmFqdnTemplate.replace(REALM_FQDN_PLACEHOLDER, toSubdomain(id));

/** Constructs the API URL from an FQDN. */
const toApiUrl = (fqdn: string): string => `https://${fqdn}${API_PATH}`;

/** Constructs the App URL from an FQDN. */
const toAppUrl = (fqdn: string): string => `https://${fqdn}${APP_PATH}`;

/** Constructs the MCP URL from an FQDN. */
const toMcpUrl = (fqdn: string): string => `https://${fqdn}${MCP_PATH}`;

/** Constructs the URL of the bash TUI launcher script for an FQDN. */
const toTuiBashUrl = (fqdn: string): string => `https://${fqdn}${TUI_PATH}/run.sh`;

/** Constructs the URL of the PowerShell TUI launcher script for an FQDN. */
const toTuiPowershellUrl = (fqdn: string): string => `https://${fqdn}${TUI_PATH}/run.ps1`;

/**
 * Parses a RealmId from canonical encoding or throws a RealmIdError.
 * (Accepts only `/^R[0-9A-F]{2}$/i`.)
 */
const fromString = (str: string): RealmId => {
  if (!/^R[0-9A-F]{2}$/i.test(str)) throw new RealmIdError("Format", `String ${str} is not a valid RealmId.`);
  const n = parseInt(str.slice(1), 16);
  return fromNumber(n);
};

/**
 * Template tag for interpolating RealmId values in template literals.
 * Example: f`Realm${realmId}Edge` → "RealmRFFEdge"
 */
const f = (strings: TemplateStringsArray, ...values: RealmId[]): string =>
  (strings[0] ?? "") + values.map((v, i) => toString(v) + (strings[i + 1] ?? "")).join("");

export const RealmId = {
  fromNumber,
  toNumber,
  toString,
  toSubdomain,
  toFqdn,
  toApiUrl,
  toAppUrl,
  toMcpUrl,
  toTuiBashUrl,
  toTuiPowershellUrl,
  fromString,
  f,
  API_PATH,
  APP_PATH,
  MCP_PATH,
  TUI_PATH,
} as const;
